// Firebase removed - using local data loading
import { getSefirasHaomerContent } from '../../calendar/sefirasHaomer.js';
import { getThreeKapitelsContent } from '../../calendar/elulTehillim.js';
import { getVisibleSchedules, getPrintSchedules } from '../../utils/dataLogic';
import { getDate } from '../../utils/dateUtils';
import { zmanLoader } from '../../calendar/zmanim.js';
import { logger } from '../../utils/kioskLogger';
import { safeReload } from '../../utils/safeReload';

function isAfterTzeisFromZmanim(zmanimData) {
  const tzeis = zmanimData.find(z => z.zman === 'TZEIS');
  if (tzeis && tzeis.time) {
    return getDate() > tzeis.time;
  }
  return false;
}

/**
 * Gets the Sefirah data if it should be displayed
 * @param {Object} displayOptions - Display options from the schedule data
 * @param {Array} zmanimData - Array of zmanim data
 * @returns {Array|undefined} - Array of sefirah content lines or undefined
 */
export function getSefirahData(displayOptions, zmanimData) {
  if (displayOptions.showSefirasHaomer && zmanimData.length > 0) {
    const omerContent = getSefirasHaomerContent(getDate(), isAfterTzeisFromZmanim(zmanimData));
    console.log('omerContent', omerContent);
    return omerContent;
  }
}

/**
 * Gets the 3 kapitels Tehillim lines if the setting is on and we are in season.
 * Empty / undefined outside 1 Elul–Yom Kippur (same pattern as Omer).
 * @param {Object} displayOptions
 * @param {Array} zmanimData
 * @returns {Array|undefined}
 */
export function getThreeKapitelsData(displayOptions, zmanimData) {
  if (displayOptions.showThreeKapitels && zmanimData.length > 0) {
    return getThreeKapitelsContent(getDate(), isAfterTzeisFromZmanim(zmanimData));
  }
}

/**
 * Loads updates from Firebase or local file
 * @param {Function} setScheduleData - State setter for schedule data
 * @param {Function} setPrintSchedule - State setter for print schedule
 * @param {Function} setUpcomingData - State setter for upcoming data
 * @param {Function} setNewsData - State setter for news data
 * @param {Function} setZmanim - State setter for zmanim data
 * @param {Function} setParsha - State setter for parsha
 * @param {Function} setWeeklyNotices - State setter for weekly notices
 * @param {Function} setAnnouncements - State setter for announcements
 * @param {Function} setDisplayOptions - State setter for display options
 * @param {Function} setSefirahContent - State setter for sefirah content
 * @param {Function} setThreeKapitelsContent - State setter for 3 kapitels Tehillim
 * @param {Object} buildNumberRef - Ref for build number
 * @param {Object} errorCountRef - Ref for error count
 * @returns {Promise<Object>} - Promise resolving to an object with loaded data
 */
export async function loadUpdates(
  setScheduleData,
  setPrintSchedule,
  setUpcomingData,
  setNewsData,
  setZmanim,
  setParsha,
  setWeeklyNotices,
  setAnnouncements,
  setDisplayOptions,
  setSefirahContent,
  setThreeKapitelsContent,
  buildNumberRef,
  errorCountRef
  // storage parameter removed - using local data loading
) {
  let returnData = {};
  const fetchStartTime = Date.now();
  const isOnline = navigator.onLine;
  
  try {
    logger.info('Starting schedule data fetch', { 
      isOnline,
      errorCount: errorCountRef.current 
    });

    // Load from local file (Firebase removed) with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    let response;
    try {
      // Same URL every time so the service worker can match cached schedule offline (ignoreSearch).
      response = await fetch('/schedule.json', {
        signal: controller.signal,
        cache: 'default',
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Fetch timeout after 10 seconds');
      }
      throw fetchError;
    }
    
    const fetchDuration = Date.now() - fetchStartTime;
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    console.log('Schedule loaded from local file');

    // Validate data structure before processing
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid schedule data: not an object');
    }
    
    if (!data.weeklySchedules || typeof data.weeklySchedules !== 'object') {
      throw new Error('Invalid schedule data: missing weeklySchedules');
    }

    let zmanimObj = zmanLoader(getDate());
    returnData.zmanim = zmanimObj.zmanim;

    setZmanim(zmanimObj.zmanim);
    setNewsData(data.news);
    setWeeklyNotices(data.weeklyNotices);
    setAnnouncements(data.announcements);
    setDisplayOptions(data.displayOptions || {});

    // Get schedules including the full weekly schedule for printing.
    const { visibleSchedule, upcomingSchedule, parsha, chazak } = getVisibleSchedules(data, zmanimObj);
    const printSchedule = getPrintSchedules(getDate(), data, zmanimObj);

    // Validate visibleSchedule before setting state
    if (!visibleSchedule || !visibleSchedule.schedule || !Array.isArray(visibleSchedule.schedule)) {
      logger.error('Invalid visibleSchedule structure', {
        visibleSchedule,
        hasVisibleSchedule: !!visibleSchedule,
        visibleScheduleType: typeof visibleSchedule,
        visibleScheduleKeys: visibleSchedule ? Object.keys(visibleSchedule) : [],
        hasRawData: !!data,
        rawDataKeys: data ? Object.keys(data) : [],
        hasDatedSchedules: !!data?.datedSchedules,
        datedSchedulesCount: data?.datedSchedules ? Object.keys(data.datedSchedules).length : 0,
        hasZmanim: zmanimObj.zmanim?.length > 0
      });
      throw new Error('Invalid visibleSchedule: missing schedule array');
    }

    // Log if schedule is empty (but structure is valid)
    const hasScheduleItems = visibleSchedule.schedule.length > 0;
    if (!hasScheduleItems) {
      logger.warn('Visible schedule is empty after processing', {
        hasVisibleSchedule: !!visibleSchedule,
        visibleScheduleKeys: visibleSchedule ? Object.keys(visibleSchedule) : [],
        hasRawData: !!data,
        rawDataKeys: data ? Object.keys(data) : [],
        hasDatedSchedules: !!data?.datedSchedules,
        datedSchedulesCount: data?.datedSchedules ? Object.keys(data.datedSchedules).length : 0,
        hasZmanim: zmanimObj.zmanim?.length > 0
      });
    }

    setParsha(parsha);

    // Only update state if we have valid data
    setScheduleData(visibleSchedule);
    setUpcomingData(upcomingSchedule || {});
    setPrintSchedule(printSchedule);
    returnData.visibleSchedule = visibleSchedule;
    returnData.upcomingSchedule = upcomingSchedule;
    returnData.chazak = chazak;

    setSefirahContent(getSefirahData(data.displayOptions, zmanimObj.zmanim));
    setThreeKapitelsContent(getThreeKapitelsData(data.displayOptions, zmanimObj.zmanim));

    const currentBuildNumber = data?.manifest?.build_number;
    if (currentBuildNumber !== undefined) {
      if (buildNumberRef.current === null) {
        buildNumberRef.current = currentBuildNumber;
      } else if (buildNumberRef.current !== currentBuildNumber) {
        console.log('Build number changed, attempting safe reload...');
        logger.info('Build number changed, attempting safe reload', {
          oldBuild: buildNumberRef.current,
          newBuild: currentBuildNumber
        });
        // Only reloads once the new version is fully downloaded (or skips when
        // offline). buildNumberRef is intentionally left unchanged so a skipped
        // attempt retries on the next polling cycle.
        await safeReload({ reason: 'build_number', needNewBuild: true });
      }
    }

    errorCountRef.current = 0;
    
    // Log successful fetch
    logger.info('Schedule data loaded successfully', {
      fetchDuration,
      hasSchedule: !!visibleSchedule?.schedule,
      scheduleItemsCount: visibleSchedule?.schedule?.length || 0,
      hasZmanim: zmanimObj.zmanim?.length > 0,
      buildNumber: currentBuildNumber
    });
  } catch (error) {
    const fetchDuration = Date.now() - fetchStartTime;
    const errorMessage = error.message || 'Unknown error';
    const errorName = error.name || 'Error';
    
    console.error('Error fetching /schedule.json:', error);
    
    // Log the error with details
    logger.error('Failed to fetch schedule data', {
      error: errorMessage,
      errorName,
      fetchDuration,
      isOnline,
      errorCount: errorCountRef.current + 1,
      hasScheduleData: !!returnData.visibleSchedule,
      // Include error stack if available (but limit length)
      stack: error.stack ? error.stack.substring(0, 500) : undefined
    });
    
    errorCountRef.current += 1;
    if (errorCountRef.current > 5) {
      logger.warn('Too many errors, attempting safe reload', {
        errorCount: errorCountRef.current
      });
      console.log('Too many errors, attempting safe reload...');
      const reloaded = await safeReload({ reason: 'too_many_errors', needNewBuild: false });
      if (!reloaded) {
        // Could not safely reload (offline with no cached shell). Keep serving
        // the current content and reset the counter so we do not spin; we will
        // try again after another run of consecutive failures.
        errorCountRef.current = 0;
      }
    }
  }
  return returnData;
}
